import { getEnv } from '@unni/platform'

import { SearchService } from '@/features/search/api/search.service'
import { createSolarApiClient } from '@/shared/libs/api-client'
import { User } from '@/features/member/member.type'
import { convertFromLegacySearch } from '@/features/search/utils/contract-converter'
import { getServerTranslation } from '@/utils/i18n/i18n-server'
import { getSearchDistrictCodes, Locales } from '@/utils/language'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import { getDefaultMeta, SEOMeta } from '@/shared/utils'
import { TreatmentCategoryFilterService } from '@/features/treatment-category-filter/api/treatment-category-filter.service'
import Search from '@/features/search/screen/search'

import type { LegacySearch } from '@/common/models/search'
import type { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'

interface Props extends LegacySearch {
  user: User | null
  keyword: string
  treatmentCategoryName: string
  treatmentCategoryId: number
  token: string
  language: string
  blogs: Post[]
  blogTotalLength: number
  meta: SEOMeta
}

const SearchPage: NextPage<Props> = (props) => {
  return <Search {...props} />
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext): Promise<GetServerSidePropsResult<Props>> => {
  try {
    const contextEnhancer = new ContextEnhancer(ctx)
    const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
    const { t } = getServerTranslation(enhancedContext.locale)
    const category = ctx.query.category ? decodeURIComponent(ctx.query.category as string) : ''
    const q = ctx.query.q ? decodeURIComponent(ctx.query.q as string) : ''

    const treatmentCategoryFilterService = new TreatmentCategoryFilterService(
      createSolarApiClient({ headers: enhancedContext.apiHeaders }),
    )
    const treatmentCategoryFilterResponse = await treatmentCategoryFilterService.getTreatmentCategoryFilter()
    const treatmentCategories = treatmentCategoryFilterResponse.treatmentCategories

    const treatmentCategory = treatmentCategories.find((item) => item.name === category)
    const keyword = ((q || treatmentCategory?.name) as string) || ''

    const searchPromise = () => {
      const searchService = new SearchService(
        createSolarApiClient({
          headers: {
            ...enhancedContext.apiHeaders,
            'user-account-id': `${enhancedContext.user.id}`,
            'user-log-tracking-id': `${enhancedContext.user.id}`,
          },
        }),
      )
      return searchService.searchAll({
        keyword,
        filters: {
          hospital: {
            district: {
              neighborhoodVicinityCodes: getSearchDistrictCodes(enhancedContext.locale, { isCrossCountry: true }),
            },
          },
        },
        pagination: {
          pageIndex: 0,
          pageSize: 20,
          sort: 'RECOMMENDATION',
        },
      })
    }

    const results = await Promise.allSettled([searchPromise()])

    const newSearch = results[0].status === 'fulfilled' ? results[0].value : null

    if (!newSearch) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      }
    }
    const {
      totalLength,
      serviceOfferTotalLength,
      communityDocumentTotalLength,
      hospitalTotalLength,
      treatmentTotalLength,
      reviewTotalLength,
      serviceOfferItems,
      communityDocuments,
      reviews,
      hospitals,
      treatments,
    } = convertFromLegacySearch(newSearch, enhancedContext.locale)

    const meta = getDefaultMeta(enhancedContext.locale, {
      ...getMeta(t, enhancedContext.locale, keyword, totalLength),
      canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}/search${keyword ? `?q=${keyword}` : ''}`,
    })

    /**
     * PANAMA SEO robot 수집 정책
     * : 이벤트/커뮤니티/병원/리뷰의 검색 결과가 6개 미만일 경우 저평가 페이지로 규정하여 검색 결과를 노출하지 않음
     */
    if (serviceOfferTotalLength + communityDocumentTotalLength + hospitalTotalLength + reviewTotalLength < 6) {
      ctx.res.setHeader('x-robots-tag', 'noindex, follow')
    }

    return {
      props: {
        user: enhancedContext.user,
        keyword,
        treatmentCategoryName: treatmentCategory?.name || '',
        treatmentCategoryId: treatmentCategory?.id || 0,
        totalLength,
        serviceOfferTotalLength,
        communityDocumentTotalLength,
        hospitalTotalLength,
        treatmentTotalLength,
        reviewTotalLength,
    reviews: enhancedContext.isAnonymousUser && enhancedContext.locale !== Locales.JA_JP ? [] : reviews,
        serviceOfferItems: serviceOfferItems.slice(0, 5),
        communityDocuments: communityDocuments.slice(0, 5),
        hospitals: (hospitals || []).slice(0, 3),
        treatments,
        language: enhancedContext.locale,
        token: enhancedContext.token,
        blogs: [],
        blogTotalLength: 0,
        meta: {
          ...meta,
          canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}/search${keyword ? `?q=${keyword}` : ''}`,
        },
      },
    }
  } catch (error) {
    console.error(error)
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    }
  }
}

const getMeta = (t: (key: string) => string, locale: Locales, keyword: string, totalLength: number): SEOMeta => {
  const year = new Date(Date.now()).getFullYear()

  const META: Record<Locales, SEOMeta> = {
    [Locales.JA_JP]: {
      title: `${keyword}の施術・口コミ・クリニック情報(${year}) | カンナムオンニ`,
      description: `${keyword}に関する美容医療・整形の値段・施術解説・口コミ・クリニック情報 ${totalLength}件. 日本最大級の美容医療・整形の口コミ予約アプリ.`,
    },
    [Locales.KO_KR]: {
      title: `${keyword}에 대한 시술・리뷰・병원 정보(${year}) | 강남언니`,
      description: `${keyword}에 대한 시술·성형의 가격·시술 정보 및 리뷰·병원 정보 ${totalLength}개. 국내 1위 미용의료앱 강남언니. 리얼후기부터 가격비교, 상담예약까지 한번에.`,
    },
    [Locales.EN_US]: {
      title: `${keyword} about cosmetic surgery, plastic surgery, price, treatment information, review, hospital information (${year}) | Gangnamunni`,
      description: `${keyword} about cosmetic surgery, plastic surgery, price, treatment information, review, hospital information ${totalLength} items. The most popular cosmetic surgery app in the US. From real reviews to price comparisons, consultations, and reservations, everything in one place.`,
    },
    [Locales.TH_TH]: {
      title: `รีวิวจริง・โปรโมชั่น・ราคาที่ดีที่สุดของ ${keyword} | UNNI - แอปเกาหลีอันดับ 1`,
      description: `รวมข้อมูลทั้งหมดของ ${keyword}! ครบทั้งรีวิว&โปรโมชั่นกว่า ${totalLength} รายการ! ปรึกษาและจองคลินิกได้ง่ายๆ กับ UNNI`,
    },
    [Locales.ZH_HANS_CN]: {
      title: `${keyword} 治疗・评论・医院信息 (${year}) | Gangnamunni`,
      description: `${keyword} 治疗・评论・医院信息 ${totalLength} 条. 中国最受欢迎的医美应用。从真实评论到价格比较、咨询预约，一切尽在掌握。`,
    },
    [Locales.ZH_HANT_TW]: {
      title: `${keyword} 治療・評論・醫院信息 (${year}) | Gangnamunni`,
      description: `${keyword} 治療・評論・醫院信息 ${totalLength} 條. 最受歡迎的醫美應用。從真實評論到價格比較、諮詢預約，一切盡在掌握。`,
    },
  }

  return {
    twtCardType: t('common.meta.twtCardType'),
    type: t('common.meta.type'),
    ...(META[locale] ?? META[Locales.KO_KR]),
  }
}

export default SearchPage
